import { useCallback } from "react";
import { trpc } from "../../../lib/api/trpc";
import { logError } from "../../../utils/errors/errorHandler";
import { useAuth } from "../../../hooks";

/**
 * Hook for profile management operations
 *
 * Provides methods for updating user profile information
 */

interface UpdateProfileInput {
  firstName?: string;
  lastName?: string;
  email?: string;
}

interface ProfileUpdateResult {
  success: boolean;
  error?: string;
  data?: any;
}

export const useProfile = () => {
  const { user, refreshUser } = useAuth();

  // Update profile mutation
  // @ts-ignore - Type checking for trpc.user property
  const {
    mutateAsync: updateProfileAsync,
    isLoading,
    isError,
    error,
    isSuccess,
    reset,
  } = trpc.user.updateUser.useMutation({
    onSuccess: () => {
      // Re-fetch user data via AuthProvider so context state updates
      refreshUser();
    },
  });

  // Function to update profile with better error handling
  const updateProfile = useCallback(
    async (data: UpdateProfileInput): Promise<ProfileUpdateResult> => {
      if (!user?.id) {
        return {
          success: false,
          error: "User not authenticated",
        };
      }

      try {
        const result = await updateProfileAsync({
          id: user.id,
          ...data,
        });

        return { success: true, data: result };
      } catch (err: any) {
        logError(err, "Update Profile");
        return {
          success: false,
          error: err?.message || "Failed to update profile",
        };
      }
    },
    [updateProfileAsync, user?.id],
  );

  return {
    updateProfile,
    isLoading,
    isError,
    error,
    isSuccess,
    reset,
  };
};
